import { useState } from 'react';
import c from './styles';
import { statusColors, highlight, fmtAmt, fmtDate } from './formatters';

/* ── UI primitives ───────────────────────────────────────────── */
export function Card({ children, style, accent }) {
  return (
    <div className="txn-card" style={{ ...c.card, ...(accent ? { borderTop: `3px solid ${accent}` } : {}), ...style }}>
      {children}
    </div>
  );
}

export function SectionTitle({ icon, title, right }) {
  return (
    <div className="txn-section-title" style={c.sectionTitle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon && <div style={c.sectionIcon}>{icon}</div>}
        <span style={c.sectionTitleText}>{title}</span>
      </div>
      {right}
    </div>
  );
}

export function KVRow({ label, value, isStatus, isLast, isMono, onClick }) {
  const fmtVal = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) { try { return new Date(s).toLocaleString('en-IN'); } catch {} }
    return s || '—';
  };
  const sc = isStatus ? statusColors(String(value)) : null;
  return (
    <div className="txn-kv-row" style={{ ...c.kvRow, ...(isLast ? {} : { borderBottom: '1px solid #f8fafc' }) }}>
      <span style={c.kvLabel}>{label}</span>
      {isStatus && sc ? (
        <span style={{ ...c.statusBadge, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
          <span style={{ ...c.dot, background: sc.dot }} />{fmtVal(value)}
        </span>
      ) : onClick ? (
        <span onClick={onClick} style={{ ...c.kvVal, fontFamily: isMono ? 'monospace' : 'inherit', fontSize: isMono ? 12 : 13, color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{fmtVal(value)}</span>
      ) : (
        <span style={{ ...c.kvVal, fontFamily: isMono ? 'monospace' : 'inherit', fontSize: isMono ? 12 : 13 }}>{fmtVal(value)}</span>
      )}
    </div>
  );
}

/* ── RoutingAttempts ─────────────────────────────────────────── */
export function RoutingAttempts({ meta, onCopy }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [javaJsonOpen, setJavaJsonOpen] = useState({});
  const attempts = meta.ROUTING_ATTEMPTS || [];

  const provColor = (s) => {
    const u = (s ?? '').toUpperCase();
    if (['SUCCESS','ACCEPTED','TXN'].includes(u)) return { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0' };
    if (['FAILED','ERR','ERROR'].includes(u))      return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' };
    return { bg: '#fffbeb', color: '#d97706', border: '#fde68a' };
  };

  const tryJson = (s) => { try { return JSON.parse(s); } catch { return null; } };
  const tryJava = (s) => {
    if (!s) return null;
    const m = s.match(/^(\w+)\((.+)\)$/s); if (!m) return null;
    const obj = {};
    m[2].split(',').forEach(p => { const i = p.indexOf('='); if (i > -1) { const k = p.slice(0, i).trim(); if (k) obj[k] = p.slice(i + 1).trim(); } });
    return obj;
  };
  const tryJavaObj = (s) => {
    if (!s || typeof s !== 'string') return null;
    const t = s.trim();
    if (!t.startsWith('{') || !t.endsWith('}')) return null;
    const parseObj = (inner) => {
      const obj = {}; let idx = 0;
      while (idx < inner.length) {
        while (idx < inner.length && inner[idx] === ' ') idx++;
        if (idx >= inner.length) break;
        const eq = inner.indexOf('=', idx); if (eq === -1) break;
        const key = inner.slice(idx, eq).trim(); idx = eq + 1;
        if (inner[idx] === '{') {
          let depth = 0, start = idx;
          while (idx < inner.length) { if (inner[idx] === '{') depth++; else if (inner[idx] === '}') { if (--depth === 0) { idx++; break; } } idx++; }
          obj[key] = parseObj(inner.slice(start + 1, idx - 1));
        } else if (inner[idx] === '[') {
          let depth = 0, start = idx;
          while (idx < inner.length) { if (inner[idx] === '[') depth++; else if (inner[idx] === ']') { if (--depth === 0) { idx++; break; } } idx++; }
          const arrInner = inner.slice(start + 1, idx - 1).trim();
          if (arrInner.startsWith('{')) {
            const items = []; let j = 0;
            while (j < arrInner.length) {
              while (j < arrInner.length && arrInner[j] !== '{') j++;
              if (j >= arrInner.length) break;
              let d = 0, s2 = j;
              while (j < arrInner.length) { if (arrInner[j] === '{') d++; else if (arrInner[j] === '}') { if (--d === 0) { j++; break; } } j++; }
              items.push(parseObj(arrInner.slice(s2 + 1, j - 1)));
            }
            obj[key] = items.length === 1 ? items[0] : items;
          } else { obj[key] = arrInner; }
        } else {
          const start = idx; while (idx < inner.length && inner[idx] !== ',') idx++;
          obj[key] = inner.slice(start, idx).trim();
        }
        if (idx < inner.length && inner[idx] === ',') idx++;
      }
      return obj;
    };
    try { return parseObj(t.slice(1, -1)); } catch { return null; }
  };

  const fmtTs = (ts) => { try { const d = new Date(ts); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return String(ts); } };

  return (
    <div>
      {(meta.PROVIDER || meta.STATUS || meta.STATUS_CODE) && (
        <div style={c.routingMeta}>
          {[['PROVIDER', meta.PROVIDER], ['STATUS', meta.STATUS], ['STATUS CODE', meta.STATUS_CODE], ['TXN ID', meta.AERON_TXN_ID || meta.TXN_ID]].filter(([,v]) => v != null).map(([lbl, val]) => (
            <div key={lbl} style={c.routingMetaCell}>
              <div style={c.routingMetaLabel}>{lbl}</div>
              <div style={c.routingMetaValue}>{String(val)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {attempts.map((a, i) => {
          const SKIP = new Set(['provider','status','timestamp','response']);
          const sc2 = provColor(a.status);
          const extras = Object.entries(a).filter(([k]) => !SKIP.has(k));
          const hasContent = extras.length > 0 || !!a.response;
          const isOpen = openIdx === i;
          return (
            <div key={i} style={c.attemptRow}>
              <div style={{ ...c.attemptHeader, cursor: hasContent ? 'pointer' : 'default' }} onClick={() => hasContent && setOpenIdx(isOpen ? null : i)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={c.attemptNum}>{i + 1}</div>
                  <span style={c.attemptProvider}>{a.provider}</span>
                  <span style={{ ...c.attemptStatus, background: sc2.bg, color: sc2.color, border: `1px solid ${sc2.border}` }}>{a.status}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {a.timestamp && <span style={c.attemptTs}>{fmtTs(a.timestamp)}</span>}
                  {hasContent && <span style={c.attemptChevron}>{isOpen ? '▲' : '▼'}</span>}
                </div>
              </div>
              {isOpen && (
                <div style={c.attemptBody}>
                  {extras.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#f1f5f9' }}>
                      {extras.map(([k, v]) => (
                        <div key={k} style={{ background: '#fff', padding: '8px 14px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v ?? '—')}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {a.response && (() => {
                    const parsed = tryJson(a.response);
                    const javaObj = !parsed ? tryJava(a.response) : null;
                    const javaObjParsed = !parsed && !javaObj ? tryJavaObj(a.response) : null;
                    if (parsed) return (
                      <div>
                        <div style={c.codeHeader}><span style={c.codeLabel}>RESPONSE · JSON</span><button style={c.codeCopyBtn} onClick={() => onCopy(JSON.stringify(parsed, null, 2))}>COPY</button></div>
                        <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(parsed) }} />
                      </div>
                    );
                    if (javaObj) return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#f1f5f9' }}>
                        {Object.entries(javaObj).map(([k, v]) => (
                          <div key={k} style={{ background: '#fff', padding: '8px 14px' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', wordBreak: 'break-all' }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    );
                    if (javaObjParsed) {
                      const topFields = Object.entries(javaObjParsed).filter(([, v]) => typeof v !== 'object');
                      const nestedFields = Object.entries(javaObjParsed).filter(([, v]) => typeof v === 'object' && v !== null);
                      return (
                        <div>
                          {topFields.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#f1f5f9' }}>
                              {topFields.map(([k, v]) => {
                                const isSt = k.toLowerCase().includes('status');
                                const sColor = isSt ? statusColors(String(v)) : null;
                                return (
                                  <div key={k} style={{ background: '#fff', padding: '8px 14px' }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                                    {isSt && sColor
                                      ? <span style={{ ...c.statusBadge, background: sColor.bg, color: sColor.color, border: `1px solid ${sColor.border}`, fontSize: 11 }}><span style={{ ...c.dot, background: sColor.dot }} />{v}</span>
                                      : <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', wordBreak: 'break-all' }}>{v}</div>
                                    }
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {nestedFields.map(([groupKey, nested]) => (
                            <div key={groupKey}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px 4px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>{groupKey}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#f1f5f9' }}>
                                {Object.entries(nested).map(([k, v]) => {
                                  const isSt = k.toLowerCase().includes('status');
                                  const sColor = isSt ? statusColors(String(v)) : null;
                                  return (
                                    <div key={k} style={{ background: '#fff', padding: '8px 14px' }}>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                                      {isSt && sColor
                                        ? <span style={{ ...c.statusBadge, background: sColor.bg, color: sColor.color, border: `1px solid ${sColor.border}`, fontSize: 11 }}><span style={{ ...c.dot, background: sColor.dot }} />{String(v)}</span>
                                        : <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v)}</div>
                                      }
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          <div style={c.codeBlock}>
                            <div style={{ ...c.codeHeader, cursor: 'pointer' }} onClick={() => setJavaJsonOpen(o => ({ ...o, [i]: !o[i] }))}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={c.codeSquare} /><span style={c.codeLabel}>VIEW AS JSON</span></div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <button style={c.codeCopyBtn} onClick={e => { e.stopPropagation(); onCopy(JSON.stringify(javaObjParsed, null, 2)); }}>COPY</button>
                                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 700 }}>{javaJsonOpen[i] ? '▲' : '▼'}</span>
                              </div>
                            </div>
                            {javaJsonOpen[i] && <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(javaObjParsed) }} />}
                          </div>
                        </div>
                      );
                    }
                    return <div style={{ padding: '10px 16px', fontSize: 12, color: '#92400e', background: '#fffbeb', fontStyle: 'italic' }}>{a.response}</div>;
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── StatusCheckResult ───────────────────────────────────────── */
export function StatusCheckResult({ scResult, scError, scType, scProvider, scJsonOpen, setScJsonOpen, handleCopy, isMobile }) {
  if (!scResult && !scError) return null;

  const normalizePayoutResult = (res, p = '') => {
    if (!res) return null; const P = p.toUpperCase();
    if (P.includes('AERON'))    return { status: res.status, statusCode: res.statusCode, txnId: res.transactionId, refId: res.client_referenceId, utr: res.utr, amount: res.amount, message: res.description };
    if (P.includes('TRAMO'))    return { status: res.data?.status, statusCode: res.code, txnId: res.data?.partnerTransactionId, refId: res.data?.clientRefId, utr: res.data?.vendorUtrNumber, message: res.message };
    if (P.includes('NIFI') || P.includes('NIFFI')) return { status: res.status, statusCode: res.statuscode, txnId: res.data?.txnId || res.data?.ClientTransactionId, refId: res.data?.externalRef || res.data?.refernce_no, utr: res.data?.bankRefNo || res.data?.BankRefNo, balance: res.deduction?.balance, message: res.message };
    if (P.includes('CODEGRAPHI')) return { status: res.data?.status, txnId: res.data?.bank_txn_id, refId: res.data?.refId, utr: res.data?.utr, amount: res.data?.amount, message: res.message };
    if (P.includes('EZYPAY') || P.includes('EZY')) return { status: res.status || res.txnstatus, statusCode: res.statuscode || res.code, txnId: res.txnid || res.txn_id, refId: res.requestid || res.txn_requestid, utr: res.utr || res.bankrrn, amount: res.amount, message: res.message || res.msg };
    if (P.includes('DILLI')) return { status: res.data?.status || res.status, statusCode: res.statuscode, txnId: res.data?.dp_id, refId: res.data?.client_id, utr: res.data?.op_id, message: res.message };
    if (P.includes('INSTANTPAY')) {
      const code = (res.data?.transactionStatusCode || '').toUpperCase();
      return { status: code === 'TXN' ? 'SUCCESS' : code === 'ERR' ? 'FAILED' : (res.status || code), statusCode: code || res.statuscode, txnId: res.data?.order?.referenceId || res.data?.transactionReferenceId, refId: res.data?.order?.externalRef, utr: res.data?.transactionReferenceId, amount: res.data?.transactionAmount, message: res.data?.transactionStatus || res.actcode };
    }
    if (P.includes('FINZEP')) {
      return { status: (res.msg || '').toUpperCase(), statusCode: res.errorcode, utr: res.opid?.trim() || undefined, refId: res.rpid, amount: res.amount, balance: res.bal, account: res.account, message: res.msg };
    }
    if (P.includes('SOLITECK') || P.includes('SOLITE')) {
      return { status: res.statusLabel, statusCode: res.responseCode, utr: res.utr, refId: res.soliteckTxnRef, amount: res.amount, message: res.responseMessage };
    }
    return { status: res.status, message: res.message };
  };
  const normalizeAepsResult = (res, p = '') => {
    if (!res) return null; const P = p.toUpperCase();
    if (P.includes('INSTANTPAY')) { const code = (res.data?.transactionStatusCode || '').toUpperCase(); return { status: code === 'TXN' ? 'SUCCESS' : code === 'ERR' ? 'FAILED' : (res.status || code), statusCode: code || res.statuscode, txnId: res.data?.order?.referenceId || res.data?.transactionReferenceId, refId: res.data?.order?.externalRef, utr: res.data?.transactionReferenceId, amount: res.data?.transactionAmount, message: res.data?.transactionStatus || res.actcode }; }
    if (P.includes('EZYPAY') || P.includes('EZY')) return { status: res.status || res.txnstatus, statusCode: res.statuscode || res.code, txnId: res.txnid || res.txn_id, refId: res.requestid || res.txn_requestid, utr: res.utr || res.bankrrn, amount: res.amount, message: res.message || res.msg };
    return { status: res.status, message: res.message };
  };
  const normalizeRechargeResult = (res, p = '') => {
    if (!res) return null; const P = p.toUpperCase();
    if (P.includes('EZYPAY') || P.includes('EZY')) return { status: res.status || res.txnstatus, statusCode: res.statuscode || res.code, txnId: res.txnid || res.txn_id, refId: res.requestid || res.txn_requestid, utr: res.utr || res.bankrrn, amount: res.amount, message: res.message || res.msg };
    if (P.includes('EARTHY'))  return { status: res.dataContent?.[0]?.status || res.Status || res.status, txnId: res.dataContent?.[0]?.TransactionId || res.OrderId || res.orderId, utr: res.Utr || res.utr, amount: res.dataContent?.[0]?.Amount || res.Amount || res.amount, message: res.statusMsg || res.Message || res.message };
    if (P.includes('INDICORE')) return { status: res.status, statusCode: res.statusCode, txnId: res.txnId, utr: res.utr, amount: res.amount, message: res.message };
    if (P.includes('ENKASH')) return { txnId: res.txnId, refId: res.externalRefNo || null, message: res.note || res.message || null };
    if (P.includes('AVENUE') || P.includes('BBPS')) {
      const t = res.txnList || {};
      return {
        status:       t.txnStatus,
        txnId:        t.txnReferenceId,
        amount:       t.amount != null ? Number(t.amount) / 100 : null,
        customerName: t.respCustomerName,
        billerId:     t.billerId,
        txnDate:      fmtDate(t.txnDate),
        mobile:       t.mobile,
        paramName:    t.inputParams?.paramName,
        paramValue:   t.inputParams?.paramValue,
        billPeriod:   t.respBillPeriod,
        refundMsg:    res.refundTxn?.refundRespMessage,
        message:      res.responseReason,
      };
    }
    return { status: res.status || res.Status, message: res.message || res.Message };
  };

  const norm = scType === 'recharge' ? normalizeRechargeResult(scResult, scProvider)
             : scType === 'aeps'     ? normalizeAepsResult(scResult, scProvider)
             : scType === 'pg'       ? (() => {
                 if ((scProvider || '').toUpperCase().includes('FINKEDA')) {
                   const txn = scResult?.apiResponseData?.data?.[0] || scResult?.apiResponseData?.responseData || {};
                   return {
                     status:      txn.status,
                     txnId:       txn.transactionID || txn.orderId,
                     amount:      txn.amount,
                     cardBank:    txn.cardBank,
                     cardNetwork: [txn.cardNetwork, txn.cardType].filter(Boolean).join(' / ') || null,
                     pgStatus:    txn.pgStatus,
                     message:     scResult?.apiResponseMessage,
                   };
                 }
                 const d = scResult.data || scResult;
                 return {
                   status:    d.status,
                   txnId:     d.orderid || d.order_id || d.gateway_orderid,
                   utr:       d.utr,
                   amount:    d.amount,
                   currency:  d.currency,
                   updatedAt: d.updated_at,
                   message:   scResult.message,
                 };
               })()
             : normalizePayoutResult(scResult, scProvider);
  const ps  = String(norm?.status || '').toUpperCase();
  const psc = statusColors(ps);
  const SyncIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.73-4.59"/></svg>;
  const fields = [
    ps                       && ['STATUS',             ps,                            false, true  ],
    norm?.statusCode != null && ['STATUS CODE',        String(norm.statusCode),        false, false ],
    norm?.txnId              && ['TXN ID',             norm.txnId,                    true,  false ],
    norm?.utr                && ['UTR / BANK REF',     String(norm.utr).toUpperCase(), true,  false ],
    norm?.refId              && ['REF ID',             norm.refId,                    true,  false ],
    norm?.amount != null     && ['AMOUNT',             `₹${fmtAmt(norm.amount)}`,     false, false ],
    norm?.currency           && ['CURRENCY',           norm.currency,                 false, false ],
    norm?.account            && ['ACCOUNT',            norm.account,                  true,  false ],
    norm?.cardBank           && ['CARD BANK',          norm.cardBank,                 false, false ],
    norm?.cardNetwork        && ['CARD',               norm.cardNetwork,              false, false ],
    norm?.pgStatus           && ['PG STATUS',          norm.pgStatus,                 false, false ],
    norm?.customerName       && ['CUSTOMER NAME',      norm.customerName,             false, false ],
    norm?.mobile             && ['MOBILE',             norm.mobile,                   true,  false ],
    norm?.billerId           && ['BILLER ID',          norm.billerId,                 true,  false ],
    norm?.txnDate            && ['TXN DATE',           norm.txnDate,                  false, false ],
    norm?.paramName          && ['PARAM',              `${norm.paramName}: ${norm.paramValue}`, false, false ],
    norm?.billPeriod         && ['BILL PERIOD',        norm.billPeriod,               false, false ],
    norm?.updatedAt          && ['UPDATED AT',         norm.updatedAt,                false, false ],
    norm?.message            && ['TRANSACTION STATUS', norm.message,                  false, false ],
    norm?.refundMsg          && ['REFUND STATUS',      norm.refundMsg,                false, false ],
  ].filter(Boolean);

  return (
    <div style={{ marginTop: 20 }}>
      <Card accent="#4f46e5">
        <SectionTitle icon={<SyncIcon />} title="Provider Status Check" />
        {scError && <div style={c.errorBar}>⚠ {scError}</div>}
        {scResult && (
          <div className="txn-card-inner" style={{ padding: '0 20px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#64748b' }}><span style={{ fontWeight: 700, color: '#0f172a', marginRight: 6 }}>{scProvider}</span>{norm?.message}</div>
              {ps && <span style={{ ...c.statusBadge, background: psc.bg, color: psc.color, border: `1px solid ${psc.border}` }}><span style={{ ...c.dot, background: psc.dot }} />{ps}</span>}
            </div>
            {fields.length > 0 && (
              <div style={isMobile
                ? { display: 'flex', overflowX: 'auto', gap: 1, background: '#f1f5f9', borderRadius: 10, border: '1px solid #e2e8f0', scrollbarWidth: 'none' }
                : { display: 'grid', gridTemplateColumns: `repeat(${Math.min(fields.length, 4)}, 1fr)`, gap: 1, background: '#f1f5f9', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {fields.map(([lbl, val, mono, isSt]) => {
                  const fsc = isSt ? statusColors(val) : null;
                  return (
                    <div key={lbl} style={{ background: '#fff', padding: '12px 16px', ...(isMobile ? { flexShrink: 0, minWidth: 110 } : {}) }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>{lbl}</div>
                      {isSt && fsc
                        ? <span style={{ ...c.statusBadge, background: fsc.bg, color: fsc.color, border: `1px solid ${fsc.border}`, fontSize: 11 }}><span style={{ ...c.dot, background: fsc.dot }} />{val}</span>
                        : <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{val}</div>
                      }
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ ...c.codeBlock, marginTop: 12 }}>
              <div style={{ ...c.codeHeader, cursor: 'pointer' }} onClick={() => setScJsonOpen(o => !o)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={c.codeSquare} /><span style={c.codeLabel}>RAW RESPONSE · JSON</span></div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button style={c.codeCopyBtn} onClick={e => { e.stopPropagation(); handleCopy(JSON.stringify(scResult, null, 2)); }}>COPY</button>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 700 }}>{scJsonOpen ? '▲' : '▼'}</span>
                </div>
              </div>
              {scJsonOpen && <pre style={c.codePre} dangerouslySetInnerHTML={{ __html: highlight(scResult) }} />}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ── Icons ───────────────────────────────────────────────────── */
export const TxnChipIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 16V4m0 0L3 8m4-4l4 4"/><path d="M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>;
export const StatusChipIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
export const CodeChipIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>;
export const MsgChipIcon    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
export const IdIcon         = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8a2 2 0 0 0-2 2v2h12V5a2 2 0 0 0-2-2z"/></svg>;
export const MerchantIcon   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
export const BeneIcon       = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
export const DetailIcon     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
export const CommIcon       = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
export const RefundIcon     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>;
export const P2PIcon        = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><path d="M7 16V4m0 0L3 8m4-4l4 4"/><path d="M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>;
export const LifecycleIcon  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
export const RoutingIcon    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
export const MetaIcon       = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>;
export const SyncIcon       = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.73-4.59"/></svg>;
